import { UNIT_SQM } from "@/constants"

/**
 * This class should replace the old basket and to reduce
 * the unnecessary data and keep it more clean
 */
class ShoppingCart {

  /** @type {Integer} */
  exhibition_id = null

  /** @type {Float} */
  width = 0.0
  /** @type {Float} */
  depth = 0.0
  /** @type {Object} */
  booth_type = null

  /** @type {String} */
  label = ''

  /** @type {String} */
  hall = ''

  /** @type {String} */
  booth_number = ''

  /** @type {Array} */
  items = []

  /** @type {Array} */
  print_items = []

  /** @type {Array} */
  print_configuration = []

  /** @type {Array} */
  booth_configuration = []
  /** @type {Object} */
  booth = null

  booth_draft = null

  /** @type {Integer} */
  group = 0

  /** @type {Integer} */
  changes = 0


  /** @type {Object} */
  costs = {
    booth: 0.0
  }

  /**
   * 
   * @param {Object} data
   */
  constructor(data) {
    this._init(data)
  }

  _init(data) {
    for (const key in data) {
      if (Object.hasOwnProperty.call(data, key)) {
        this[key] = data[key]
      }
    }
    this.updateCosts()
  }

  reset() {
    this.width = 0.0
    this.depth = 0.0
    this.booth_type = null
    this.label = ''
    this.hall = ''
    this.booth_number = ''
    this.items = []
    this.print_items = []
    this.print_configuration = []
    this.booth_configuration = []
    this.booth = null
    this.booth_draft = null
    this.group = 0

    this.changes = 0

    this.costs = {
      booth: 0.0
    }
  }

  getSquareMeter() {
    return parseFloat(this.width) * parseFloat(this.depth)
  }

  increaseWidth(value) {
    this.setWidth(this.width + value)
  }

  decreaseWidth(value) {
    this.setWidth(this.width - value)
  }

  increaseDepth(value) {
    this.setDepth(this.depth + value)
  }

  decreaseDepth(value) {
    this.setDepth(this.depth - value)
  }

  setWidth(value) {
    this.width = (value >= 0) ? value : 0.0
    this.updateCosts()
    this._increaseChanges()
  }

  setDepth(value) {
    this.depth = (value >= 0) ? value : 0.0
    this.updateCosts()
    this._increaseChanges()
  }

  setWidthAndDepth(width, depth) {
    this.width = (width >= 0) ? width : 0.0
    this.depth = (depth >= 0) ? depth : 0.0
    this.updateCosts()
    this._increaseChanges()
  }

  /**
   * 
   * @param {BoothType} boothType 
   */
  setBoothType(boothType) {
    // map object without constructor so basket can be stored in local storage
    this.booth_type = {}

    for (const key in boothType) {
      if (Object.hasOwnProperty.call(boothType, key)) {
        this.booth_type[key] = boothType[key]
      }
    }
    this.updateCosts()
  }

  addArticle(article) {
    // check if article is in array
    const index = this.items.findIndex((item) => item.id === article.id)
    if (index === -1) {
      article.count = (article.unit === UNIT_SQM) ? this.getSquareMeter() : 1
      this.items.push(article)
    } else {
      this.items[index].count += 1
    }

    this._increaseChanges()
  }

  addPrintArticle(article, articlePrint) {
    const itemIndex = this.items.findIndex((item) => item.id === articlePrint.article_id)
    if (itemIndex === -1) {
      throw new Error('parent article not found')
    }
    
    let printItemIndex = this.print_items.findIndex((printItem) => {
      return (printItem.id === article.id && printItem.article_print_id === articlePrint.id)
    })

    if (printItemIndex === -1) {
      article.parent_id = articlePrint.article_id
      article.article_print_id = articlePrint.id
      article.booth_refinement_id = null
      this.print_items.push(article)
      printItemIndex = this.print_items.length - 1

      this._increaseChanges()
    }
  }

  addBoothRefinementArticle(article, boothRefinement) {
    let printItemIndex = this.print_items.findIndex((printItem) => {
      return (printItem.id === article.id && printItem.booth_refinement_id === boothRefinement.id)
    })

    if (printItemIndex === -1) {
      article.parent_id = null
      article.article_print_id = null
      article.booth_refinement_id = boothRefinement.id
      this.print_items.push(article)

      this._increaseChanges()
    }
  }

  addPrintConfiguration(temporaryFile, groupId, articlePrintId, article) {
    const index = this.print_configuration.findIndex((group) => {
      return (
        group.id === groupId
        && group.parent_id === article.parent_id
        && group.article_id === article.id
        && group.article_print_id === articlePrintId
      )
    })

    if (index === -1) {
      this.print_configuration.push({
        id: groupId,
        article_id: article.id,
        parent_id: article.parent_id,
        article_print_id: articlePrintId,
        file: temporaryFile,
      })
    } else {
      this.print_configuration.file = temporaryFile
    }
  }

  removePrintConfiguration(groupId, articlePrintId, article) {
    const index = this.print_configuration.findIndex((group) => {
      return (
        group.id === groupId
        && group.parent_id === article.parent_id
        && group.article_id === article.id
        && group.article_print_id === articlePrintId
      )
    })

    if (index === -1) {
      return
    }
    
    this.print_configuration.splice(index, 1)
    this._increaseChanges()
  }

  removeArticle(article) {
    const index = this.items.findIndex((item) => item.id === article.id)
    if (index === -1) {
      return
    }
    
    this.items[index].count -= 1
    if (this.items[index].count <= 0) {
      this.items.splice(index, 1)
    }
    this._increaseChanges()
  }

  removePrintArticle(parentArticle, articlePrintId) {
    const index = this.print_items.findIndex((printItem) => {
      return (
        printItem.parent_id === parentArticle.id
        && printItem.article_print_id === articlePrintId
      )
    })

    if (index === -1) {
      return
    }

    if (parentArticle.count < this.print_items[index].count) {
      this.print_items[index].count -= 1
    }
    if (parentArticle.count === 0) {
      this.print_items.splice(index, 1)
    }
    this._increaseChanges()
  }

  removeBoothRefinementArticle(boothRefinementId) {
    const index = this.print_items
      .findIndex((printItem) => printItem.booth_refinement_id === boothRefinementId)

    if (index === -1) {
      return
    }

    this.print_items.splice(index, 1)

    this._increaseChanges()
  }

  updateArticleAmount(article) {
    const index = this.items.findIndex((item) => item.id === article.id)
    if (index === -1) {
      this.items.push(article)
    } else {
      this.items[index].count = article.count
    }

    this._increaseChanges()
  }

  _increaseChanges() {
    this.changes++
  }

  clearBoothRefinements() {
    this.booth_configuration = []
    this.print_items = this.print_items
      .filter((printItem) => printItem.booth_refinement_id === null)
  }

  selectBooth(booth) {
    this.booth = booth
    this.clearBoothRefinements()
    this.updateCosts()
  }

  removeBooth() {
    this.booth = null
    this.clearBoothRefinements()
    this.updateCosts()
  }

  addBoothConfiguration(configuration) {
    this.booth_configuration.push(configuration)
  }

  removeBoothConfiguration(boothRefinementId) {
    this.booth_configuration = this.booth_configuration
      .filter(configuration => configuration.id !== boothRefinementId)
  }
  
  removePrintConfigurationGroup(parentArticle) {
    const group = parentArticle.count
    this.print_configuration = this.print_configuration.filter((configuration) => {
      return (
        configuration.parent_id !== parentArticle.id
        || configuration.id <= group
      )
    })
  }

  isBoothConfigurationLettering(boothRefinementId) {
    const index = this.booth_configuration.findIndex((configuration) => {
      return configuration.booth_refinement.id === boothRefinementId
    })

    if (index > -1) {
      return !this.booth_configuration[index].is_logo_print
    }

    return false
  }

  updateCosts() {
    this.costs.booth = this.calculateCostsForBooth()
  }

  calculateCostsForBooth() {
    if (this.booth === null || this.booth_type === null) {
      return 0.0
    }

    return this.getSquareMeter()
      * parseFloat(this.booth.pivot.prices[this.booth_type.type].cost_per_square_meter_net)
  }

}

export default ShoppingCart;